import { createSelector, createSlice } from '@reduxjs/toolkit'
import { adminApi } from '@/lib/api'
import { apiThunk } from '@/app/thunk'
import type { RootState } from '@/app/store'
import { toastPushed } from '@/features/ui/uiSlice'
import type { Plan, PlanBody } from '@/types'

export interface PlansState {
  items: Plan[]
  loading: boolean
  error: string | null
}

const initialState: PlansState = { items: [], loading: false, error: null }

export const fetchPlans = apiThunk<Plan[]>('plans/fetch', async (_arg, config) => {
  const rows = await adminApi.listPlans(config)
  return rows ?? []
})

export const createPlan = apiThunk<void, PlanBody>('plans/create', async (body, config, ctx) => {
  await adminApi.createPlan(config, body)
  ctx.dispatch(toastPushed('Plan "' + body.plan_name + '" created'))
  await ctx.dispatch(fetchPlans())
})

export const updatePlan = apiThunk<void, { id: number; body: PlanBody }>(
  'plans/update',
  async ({ id, body }, config, ctx) => {
    await adminApi.updatePlan(config, id, body)
    ctx.dispatch(toastPushed('Plan "' + body.plan_name + '" saved'))
    await ctx.dispatch(fetchPlans())
  }
)

export const deletePlan = apiThunk<void, { id: number; name: string }>(
  'plans/delete',
  async ({ id, name }, config, ctx) => {
    await adminApi.deletePlan(config, id)
    ctx.dispatch(toastPushed('Plan "' + name + '" deleted'))
    await ctx.dispatch(fetchPlans())
  }
)

const plansSlice = createSlice({
  name: 'plans',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.items = action.payload
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Could not load plans'
      })
  }
})

export const selectPlans = (s: RootState): Plan[] => s.plans.items

/** Only active plans can be handed to a dealer. */
export const selectAssignablePlans = createSelector([selectPlans], (plans) =>
  plans.filter((p) => p.is_active)
)

export default plansSlice.reducer
